import type { Extension } from "@medplum/fhirtypes";
import { asMemberId, type ChoiceCardinality, type ParameterValue, type UnitValue } from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";
import { PersistenceMappingError } from "../engine/errors";

/** Canonical storage contract shared by every catalog definition mapper. */
export const CATALOG_VALUE_URL = extensionUrl("catalog-value");
// 2: a Quantity's unit became a UCUM code paired with its display, where it
// had been one free-text string. A version-1 row cannot be read forward — the
// string it holds is a display with no code to derive — so the catalog is
// reseeded rather than migrated.
export const CATALOG_VALUE_SCHEMA_VERSION = 2;

export function encodeCatalogValue(value: ParameterValue): Extension {
  const parts: Extension[] = [
    { url: "schemaVersion", valueInteger: CATALOG_VALUE_SCHEMA_VERSION },
    { url: "kind", valueCode: value.kind },
  ];

  switch (value.kind) {
    case "Quantity":
      if (value.unit !== undefined) {
        parts.push({
          url: "unit",
          extension: [
            { url: "code", valueString: value.unit.code },
            { url: "display", valueString: value.unit.display },
          ],
        });
      }
      if (value.min !== undefined) parts.push({ url: "min", valueDecimal: value.min });
      if (value.max !== undefined) parts.push({ url: "max", valueDecimal: value.max });
      if (value.step !== undefined) parts.push({ url: "step", valueDecimal: value.step });
      break;
    case "Integer":
      if (value.min !== undefined) parts.push({ url: "min", valueInteger: value.min });
      if (value.max !== undefined) parts.push({ url: "max", valueInteger: value.max });
      if (value.step !== undefined) parts.push({ url: "step", valueInteger: value.step });
      break;
    case "Choice":
      parts.push(
        { url: "cardinality", valueCode: value.cardinality },
        { url: "allowFreeText", valueBoolean: value.allowFreeText },
        ...value.options.map(
          (option): Extension => ({
            url: "option",
            extension: [
              { url: "memberId", valueString: option.id },
              { url: "label", valueString: option.label },
            ],
          }),
        ),
      );
      break;
    case "Boolean":
      break;
    case "Text":
      if (value.maxLength !== undefined) parts.push({ url: "maxLength", valueInteger: value.maxLength });
      break;
  }

  return { url: CATALOG_VALUE_URL, extension: parts };
}

export function decodeCatalogValue(extension: Extension | undefined, where = "catalog value"): ParameterValue {
  if (!extension || extension.url !== CATALOG_VALUE_URL) {
    throw mappingError(where, `is missing the '${CATALOG_VALUE_URL}' extension`);
  }

  const version = requiredInteger(extension, "schemaVersion", where);
  if (version !== CATALOG_VALUE_SCHEMA_VERSION) {
    throw mappingError(where, `uses unsupported schema version '${version}'`);
  }

  const kind = requiredCode(extension, "kind", where);
  switch (kind) {
    case "Quantity": {
      const unit = optionalUnit(extension, where);
      const min = optionalDecimal(extension, "min", where);
      const max = optionalDecimal(extension, "max", where);
      const step = optionalDecimal(extension, "step", where);
      return {
        kind: "Quantity",
        ...(unit === undefined ? {} : { unit }),
        ...(min === undefined ? {} : { min }),
        ...(max === undefined ? {} : { max }),
        ...(step === undefined ? {} : { step }),
      };
    }
    case "Integer": {
      const min = optionalInteger(extension, "min", where);
      const max = optionalInteger(extension, "max", where);
      const step = optionalInteger(extension, "step", where);
      return {
        kind: "Integer",
        ...(min === undefined ? {} : { min }),
        ...(max === undefined ? {} : { max }),
        ...(step === undefined ? {} : { step }),
      };
    }
    case "Choice":
      return {
        kind: "Choice",
        cardinality: decodeCardinality(requiredCode(extension, "cardinality", where), where),
        allowFreeText: requiredBoolean(extension, "allowFreeText", where),
        options: children(extension, "option").map((option, index) => ({
          id: asMemberId(requiredString(option, "memberId", `${where} option ${index}`)),
          label: requiredString(option, "label", `${where} option ${index}`),
        })),
      };
    case "Boolean":
      return { kind: "Boolean" };
    case "Text": {
      const maxLength = optionalInteger(extension, "maxLength", where);
      return { kind: "Text", ...(maxLength === undefined ? {} : { maxLength }) };
    }
    default:
      throw mappingError(where, `has unsupported kind '${kind}'`);
  }
}

function optionalUnit(parent: Extension, where: string): UnitValue | undefined {
  const field = child(parent, "unit");
  if (!field) return undefined;
  return {
    code: requiredString(field, "code", `${where} unit`),
    display: requiredString(field, "display", `${where} unit`),
  };
}

function decodeCardinality(code: string, where: string): ChoiceCardinality {
  if (code === "Single" || code === "Multiple") return code;
  throw mappingError(where, `has unsupported choice cardinality '${code}'`);
}

function child(parent: Extension, url: string): Extension | undefined {
  return parent.extension?.find((candidate) => candidate.url === url);
}

function children(parent: Extension, url: string): Extension[] {
  return (parent.extension ?? []).filter((candidate) => candidate.url === url);
}

function requiredCode(parent: Extension, url: string, where: string): string {
  const field = child(parent, url);
  if (field?.valueCode === undefined) throw mappingError(where, `has no code '${url}'`);
  return field.valueCode;
}

function requiredString(parent: Extension, url: string, where: string): string {
  const field = child(parent, url);
  if (field?.valueString === undefined) throw mappingError(where, `has no string '${url}'`);
  return field.valueString;
}

function requiredInteger(parent: Extension, url: string, where: string): number {
  const field = child(parent, url);
  if (field?.valueInteger === undefined || !Number.isInteger(field.valueInteger)) {
    throw mappingError(where, `has no integer '${url}'`);
  }
  return field.valueInteger;
}

function requiredBoolean(parent: Extension, url: string, where: string): boolean {
  const field = child(parent, url);
  if (field?.valueBoolean === undefined) throw mappingError(where, `has no boolean '${url}'`);
  return field.valueBoolean;
}

function optionalInteger(parent: Extension, url: string, where: string): number | undefined {
  const field = child(parent, url);
  if (!field) return undefined;
  if (field.valueInteger === undefined || !Number.isInteger(field.valueInteger)) {
    throw mappingError(where, `has a non-integer '${url}'`);
  }
  return field.valueInteger;
}

function optionalDecimal(parent: Extension, url: string, where: string): number | undefined {
  const field = child(parent, url);
  if (!field) return undefined;
  if (field.valueDecimal === undefined) throw mappingError(where, `has a non-decimal '${url}'`);
  return field.valueDecimal;
}

function mappingError(where: string, problem: string): PersistenceMappingError {
  return new PersistenceMappingError(`Stored ${where} ${problem}`);
}
